import { useCallback, useEffect } from 'react';
import { t } from 'i18next';

import { useVoiceCallDirection } from './useVoiceCallDirection';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { deafPhoneNumberByModeSelector } from 'features/call/call-deaf/store';
import { deafPhoneNumberIsHiddenSelector } from 'features/deaf-phone-number-hidden/store';
import { handleError } from 'features/notification/store';
import {
  createHearingParticipant,
  createMonitoringParticipant,
} from 'features/voice-meeting/helpers';
import { useVoiceMeetingContext } from 'features/voice-meeting/hooks';
import type { VoiceSessionHearingParticipant } from 'features/voice-meeting/interfaces';
import type {
  DisconnectReason,
  Participant,
  SessionHearingParticipant,
  VoiceSessionAddParticipantRequest,
} from 'features/voice-session/interfaces';
import { VoiceSessionDaoService } from 'features/voice-session/services';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { selectIsInCallerSession } from 'features/caller-session/store';
import { LogLevel } from 'features/analytics/enums';
import { deleteVoiceSessionThunk } from 'features/voice-session/store/voiceSessionThunks';
import { voiceSessionIdSelector } from 'features/voice-session/store';

const findLastHearingParticipant = (
  participants: SessionHearingParticipant[],
  predicate: (participant: SessionHearingParticipant) => boolean
): SessionHearingParticipant | null => {
  const hearingParticipant = [...participants].reverse().find(predicate);

  return hearingParticipant ?? null;
};

const getHearingPhoneNumber = (
  hearingParticipant: SessionHearingParticipant
): string =>
  hearingParticipant.direction === 'outbound'
    ? hearingParticipant.dialString
    : hearingParticipant.fromPhoneNumber;

const adaptAddedHearingParticipant = (
  hearingParticipant: SessionHearingParticipant
): VoiceSessionHearingParticipant => ({
  id: hearingParticipant.id,
  state: hearingParticipant.state,
  disconnectReason: (hearingParticipant.disconnectReason ||
    '') as DisconnectReason,
  phoneNumber: getHearingPhoneNumber(hearingParticipant),
  direction: hearingParticipant.direction,
});

const resolveAddedHearingParticipant = ({
  phoneNumber,
  participants,
  previousParticipantIds,
}: {
  phoneNumber: string;
  participants: Participant[];
  previousParticipantIds: Set<string>;
}): SessionHearingParticipant | null => {
  const matchingHearings = participants.filter(
    (participant): participant is SessionHearingParticipant =>
      participant.type === 'hearing' &&
      getHearingPhoneNumber(participant) === phoneNumber
  );
  const newMatchingHearings = matchingHearings.filter(
    ({ id }) => !previousParticipantIds.has(id)
  );

  return (
    findLastHearingParticipant(
      newMatchingHearings,
      ({ state }) => state !== 'disconnected'
    ) ??
    findLastHearingParticipant(newMatchingHearings, () => true) ??
    findLastHearingParticipant(
      matchingHearings,
      ({ state }) => state !== 'disconnected'
    ) ??
    findLastHearingParticipant(matchingHearings, () => true)
  );
};

export const useVoiceMeetingSession = () => {
  const dispatch = useAppDispatch();
  const { service } = useVoiceMeetingContext();
  const callId = useAppSelector(callIdSelector);
  const deafPhoneNumberIsHidden = useAppSelector(
    deafPhoneNumberIsHiddenSelector
  );
  const deafPhoneNumberByMode = useAppSelector(deafPhoneNumberByModeSelector);
  const isInCallerSession = useAppSelector(selectIsInCallerSession);
  const voiceSessionId = useAppSelector(voiceSessionIdSelector);

  useVoiceCallDirection();

  // Cleans up any  chime session if for any reason it survives outside a caller session.
  useEffect(() => {
    // TODO: Consider writing a test for this effect
    if (service.meetingSession && !isInCallerSession) {
      service.tearDownLocalMeetingSession();
      dispatch(
        sendAnalyticsInfo({
          Method: 'useVoiceMeetingSession',
          Level: LogLevel.INFO,
          Message:
            'Session is not in caller session and meetingSession not null, tearing down local meeting session',
        })
      );
    }
  }, [dispatch, isInCallerSession, service]);

  const tearDownLocalMeeting = async () => {
    if (!service.meetingSession) {
      console.warn('DEBUG: Meeting session not found');
      return;
    }
    service.tearDownLocalMeetingSession();
  };

  const startMeeting = async () => {
    if (!service.meetingSession) {
      console.warn('DEBUG: Meeting session not found');
      return;
    }

    console.log('DEBUG: Step 2: Starting meeting session');
    service.meetingSession.audioVideo.start();
  };

  const addParticipant = async (phoneNumber: string) => {
    const payload = getVoiceSessionPayload(phoneNumber);
    const previousParticipantIds = new Set(
      service.voiceSession?.participants.map(({ id }) => id) ?? []
    );
    const newSessionState =
      await VoiceSessionDaoService.addParticipant(payload);
    service.setVoiceSession(newSessionState);

    const hearingParticipant = resolveAddedHearingParticipant({
      phoneNumber,
      participants: newSessionState.participants,
      previousParticipantIds,
    });

    return hearingParticipant
      ? adaptAddedHearingParticipant(hearingParticipant)
      : null;
  };

  const addMonitorParticipant = async (
    adminContact: string,
    monitorVoiceSessionId: string
  ) => {
    const participant = createMonitoringParticipant(adminContact);
    await VoiceSessionDaoService.addParticipant({
      participant,
      sessionId: monitorVoiceSessionId,
      callId,
    });
  };

  const disconnectParticipant = async ({
    participantId,
    sessionId,
    sessionParticipants,
  }: {
    participantId: string;
    sessionId: string;
    sessionParticipants: Participant[];
  }): Promise<boolean> => {
    try {
      const participantExists = sessionParticipants.some(
        (p) => p.id === participantId
      );

      if (!participantExists) {
        return false;
      }

      await VoiceSessionDaoService.deleteParticipant({
        sessionId,
        participantId,
      });
      return true;
    } catch (error) {
      dispatch(
        handleError({
          error,
          title: t('hearingDisconnectFailed'),
          methodName: 'disconnectParticipant',
        })
      );
      return false;
    }
  };

  const disconnectParticipants = async (participantIds: string[]) => {
    const sessionId = service.getSessionId();

    try {
      const voiceSession =
        await VoiceSessionDaoService.getVoiceSessionById(sessionId);

      return await Promise.all(
        participantIds.map((participantId) =>
          disconnectParticipant({
            participantId,
            sessionId,
            sessionParticipants: voiceSession.participants,
          })
        )
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'disconnectParticipants',
        })
      );
      return participantIds.map(() => false);
    }
  };

  const getSessionAndReturnHearingParticipants = useCallback(async (): Promise<
    SessionHearingParticipant[]
  > => {
    const sessionId = service.getSessionId();

    if (!sessionId) {
      return [];
    }

    try {
      const voiceSession =
        await VoiceSessionDaoService.getVoiceSessionById(sessionId);

      return voiceSession.participants.filter(
        ({ type }) => type === 'hearing'
      ) as SessionHearingParticipant[];
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'getSessionAndReturnParticipants',
        })
      );
      return [];
    }
  }, [dispatch, service]);

  const getSessionAndCheckIfDisconnected = useCallback(
    async (participantId: string) => {
      const sessionId = service.getSessionId();

      if (!sessionId) {
        return true;
      }

      try {
        const voiceSession =
          await VoiceSessionDaoService.getVoiceSessionById(sessionId);
        const participant = voiceSession.participants.find(
          (p: Participant) => p.id === participantId
        );
        return participant?.state === 'disconnected';
      } catch (error) {
        dispatch(
          handleError({
            error,
            methodName: 'getSessionAndCheckIfDisconnected',
            disableUserNotification: true,
          })
        );
        return true;
      }
    },
    [dispatch, service]
  );

  const updateVoiceSessionFromSwitchboard = useCallback(async () => {
    const sessionId = service.getSessionId();

    if (!sessionId) {
      return;
    }

    const updatedVoiceSession =
      await VoiceSessionDaoService.getVoiceSessionById(sessionId);

    service.setVoiceSession(updatedVoiceSession);
  }, [service]);

  const deleteVoiceSession = useCallback(
    async (options?: { emitCleanupIntent?: boolean }) => {
      // Prefer Redux session id so delete still works after
      // tearDownLocalMeetingSession has cleared the service's voiceSession
      // reference.
      await dispatch(deleteVoiceSessionThunk(options ?? {}));
    },
    [dispatch]
  );

  const getVoiceSessionPayload = (
    phoneNumber: string
  ): VoiceSessionAddParticipantRequest => {
    const participant = createHearingParticipant(
      phoneNumber,
      deafPhoneNumberByMode
    );
    const effectiveSessionId = service.getSessionId() ?? voiceSessionId;

    return {
      participant,
      sessionId: effectiveSessionId,
      ...(deafPhoneNumberIsHidden ? { callId } : {}),
    };
  };

  return {
    tearDownLocalMeeting,
    deleteVoiceSession,
    startMeeting,
    addParticipant,
    addMonitorParticipant,
    disconnectParticipants,
    getSessionAndCheckIfDisconnected,
    getSessionAndReturnHearingParticipants,
    updateVoiceSessionFromSwitchboard,
  };
};
